import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { PaginatedData } from './paginated-data';
@Injectable({
  providedIn: 'root',
})
export class PaginatedDataService {
  /**
   * The current pagination
   */
  public pagination$ = new BehaviorSubject<PaginatedData<any> | undefined>(undefined);

  /**
   * Whether or not the component is loading data
   */
  public working = false;

  /**
   * The last parameters used to get data
   */
  public options = RequestParametersDefault;

  /**
   * The logs parameter options to get data
   */
  public log_options = DefaultLogsRequestParameters;

  /**
   * Selected value from search
   */
  public search_value$ = new BehaviorSubject<string | number | null>(null);

  public is_all_checked$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {
    this.setDefaultPaginatedData();
  }

  /**
   * Get the a page of <T> data from the API
   * @param {string} cursor - The cursor to get the next page of data from
   * @param {RequestParameters} parameters - The parameters to send to the API
   * @param {new (data: T) => T} factory - The factory to use to create the data objects
   * @returns {Observable<PaginatedData<T>>}
   */
  public getPageAtCursor<T>(cursor: string, parameters = this.options, factory?: new (data: T) => T) {
    // We are working
    this.working = true;

    return this.http.get<PaginatedData<T>>(this.addRequestParameters(cursor, parameters)).pipe(
      // replace the response data with the data objects of type T
      map((response: PaginatedData<T>) => {
        if (factory) response.data = response.data.map((data) => new factory(data));

        // We're done working
        this.working = false;

        return response;
      }),
    );
  }

  /**
   * Add the request parameters to the URL
   * @param {string} cursor - The cursor to get the next page of data from
   * @param {RequestParameters} parameters - The parameters to send to the API
   * @returns {string}
   */
  private addRequestParameters(cursor: string, parameters = this.options): string {
    // Remember this for next time
    this.options = parameters;

    // If the cursor does not include a protocol, we will add it and the host
    if (!cursor.includes('http')) cursor = `${window.location.protocol}${cursor}`;

    // Set the data results limit
    const url = new URL(cursor);
    url.searchParams.set('limit', parameters.limit?.toString() || '10');
    url.searchParams.set('archived', parameters.archived?.valueOf().toString() || 'false');
    url.searchParams.set('deleted', parameters.deleted?.valueOf().toString() || 'false');

    // If the form is set, we will add that to the URL
    if (parameters.form) url.searchParams.set('form', parameters.form);

    // If on scheduled meetings page
    if (parameters.form === 'booking') {
      url.searchParams.set('master_page', parameters.master_page || 'scheduled');
      url.searchParams.set('label', parameters.label || 'scheduled');
    }

    if (parameters.form === 'advisors' || parameters.form === 'officeprofessionals' || parameters.form === 'associates') {
      url.searchParams.set('master_page', parameters.master_page || 'advisors');
    }
    // If scheduling is set, we will add that to the URL
    if (parameters.scheduling) url.searchParams.set('scheduling', parameters.scheduling?.valueOf().toString() || 'false');

    // Set sort by and order by
    if (parameters.sort_by) url.searchParams.set('sort_by', parameters.sort_by);
    if (parameters.order_by) url.searchParams.set('order_by', parameters.order_by);

    //pass filter id for search
    const searchValue = this.search_value$.value;
    if (searchValue) {
      url.searchParams.set('filter', `${searchValue}`);
    }

    //Add agency_bloc_agent_id to parameters
    if (parameters.agency_bloc_agent_id) url.searchParams.set('agency_bloc_agent_id', parameters.agency_bloc_agent_id.toString());

    return url.toString();
  }

  /**
   * Check all the data in the current pagination
   * @param {boolean} checked - Whether the data should be checked or not
   */
  public async checkAllPaginatedData(checked: boolean) {
    // Get the current pagination
    const pagination = await this.pagination$.value;

    // If there is a pagination, check all the data
    if (pagination) {
      for (const record of pagination.data) {
        record.checked = checked;
      }
    }

    // Update the pagination
    this.pagination$.next(pagination);
  }

  /**
   * Set default paginated param and search value
   */
  public setDefaultPaginatedData() {
    this.options = {
      limit: 15,
      archived: false,
      sort_by: 'id',
      order_by: 'desc',
      scheduling: false,
    };
    this.search_value$.next(null);
    this.pagination$.next(undefined);
  }

  /**
   * Update Order to the table
   * @param event
   * @param endpoint
   */
  public changeOrder(event: CdkDragDrop<string[]>, endpoint: string, order_endpoint: string) {
    const pagination_data = this.pagination$.value?.data;
    if (pagination_data?.length) {
      const moved_object = pagination_data[event.previousIndex];
      pagination_data.splice(event.previousIndex, 1);
      pagination_data.splice(event.currentIndex, 0, moved_object);
      pagination_data.forEach((element, index) => {
        element.order = index;
      });
      firstValueFrom(this.http.put(`${order_endpoint}/order`, this.pagination$.value?.data))
        .then(() => {
          firstValueFrom(this.getPageAtCursor(endpoint)).then((data) => {
            this.pagination$.next(data);
          });
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }
}

/**
 * The request parameters to send to the API
 */
export interface RequestParameters {
  agency_bloc_agent_id?: number;
  limit?: number;
  archived?: boolean;
  deleted?: boolean;
  form?: string;
  master_page?: string;
  label?: string;
  sort_by?: string;
  order_by?: string;
  scheduling?: boolean;
}

/**
 * The default request parameters
 */
const RequestParametersDefault: RequestParameters = {
  limit: 15,
  archived: false,
  deleted: false,
  sort_by: 'id',
  order_by: 'desc',
  scheduling: false,
};

/**
 * The default request parameters for logs
 */
const DefaultLogsRequestParameters: RequestParameters = {
  limit: 50,
};
